'use client';

import { useRouter } from 'next/navigation';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MENU_HEADER } from '@/config/menu.config';
import type { MenuItem } from '@/config/types';
import { trackHeaderNavClick } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useFilteredMenu } from '@/hooks/use-filtered-menu';
import { useLocale, useLocalePath } from '@/hooks/use-locale';
import { useMenu } from '@/hooks/use-menu';
import { useSession } from '@/hooks/use-session';
import { useWatchlist } from '@/hooks/use-watchlist';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import { MegaMenuSubApps } from '@/app/components/partials/mega-menu/mega-menu-sub-apps';

// Warm-cream link styling matched to the homepage palette. The colors are
// expressed as CSS variables (--fd-*) defined on the .fd-header wrapper, so
// the menu inherits the same ink/muted tones as the hero.
// `inline-flex flex-row items-center h-9` matches the dropdown triggers' fixed
// box (from navigationMenuTriggerStyle) so plain links (Blog, Pricing) and
// triggers (Features, API) are the exact same height AND vertically centre their
// text identically. `flex-row` is needed because this class is fed to
// NavigationMenuLink, whose base styles include `flex flex-col p-2` — without
// overriding the direction the link's text top-aligns and sits higher than the
// triggers. (Must be passed to NavigationMenuLink, not the inner link, so its
// cn() tailwind-merges these over the base instead of concatenating both.)
const LINK_CLASS = `
  fd-header-nav-link inline-flex flex-row items-center h-9 text-[16px] font-medium rounded-none px-0 py-2 border-b-2 border-transparent
  bg-transparent hover:bg-transparent focus:bg-transparent
  data-[state=open]:bg-transparent
`;

const DROPDOWN_ITEM_CLASS =
  'fd-header-dropdown-item block px-3 py-2 rounded-md text-sm';

export function MegaMenu({ fill = false }: { fill?: boolean } = {}) {
  const { t } = useTranslation();
  const { internalPathname } = useLocale();
  const localePath = useLocalePath();
  const router = useRouter();
  const { data: session } = useSession();
  const { isActive, hasActiveChild } = useMenu(internalPathname);
  const filteredMenu = useFilteredMenu(MENU_HEADER);
  const { unreadNotificationCount: watchlistUnread } = useWatchlist();

  const logHeaderClick = (button: string) => {
    // Don't pass userSub: the server resolves it from the auth cookie /
    // anon-session itself. Sending the literal string 'guest' here was
    // causing logged-in users to show up as the legacy guest sub.
    const body = JSON.stringify({ button });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/log/header-click',
        new Blob([body], { type: 'application/json' }),
      );
    } else {
      fetch('/api/log/header-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  };

  const analyticsKey = (item: MenuItem) =>
    item.analyticsKey ||
    item.path?.replace(/^\//, '').replace(/\//g, '_') ||
    item.title?.toLowerCase().replace(/\s+/g, '_') ||
    'unknown';

  const titleFor = (item: MenuItem) =>
    item.translationKey ? t(item.translationKey) : item.title ?? '';

  const handleNavClick = (item: MenuItem) => {
    logHeaderClick(analyticsKey(item));
    if (item.trackingEvent) {
      trackHeaderNavClick(
        item.trackingEvent,
        session?.user ? 'logged_in' : 'guest',
      );
    }
  };

  /**
   * Render a single child link inside a regular (non-mega) dropdown.
   * Handles `lockedWhenLoggedOut` by swapping the link for a disabled
   * button + tooltip.
   */
  const renderDropdownChild = (child: MenuItem, key: number) => {
    const locked = child.lockedWhenLoggedOut && !session?.user;
    if (locked) {
      return (
        <li key={key}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-disabled="true"
                className="fd-header-dropdown-item flex items-center justify-between gap-2 w-full px-3 py-2 rounded-md text-sm text-muted-foreground opacity-60 cursor-not-allowed"
                onClick={(e) => e.preventDefault()}
              >
                <span>{titleFor(child)}</span>
                <Lock className="size-3.5 shrink-0" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            {child.lockedTooltipKey && (
              <TooltipContent side="right" sideOffset={8}>
                {t(child.lockedTooltipKey)}
              </TooltipContent>
            )}
          </Tooltip>
        </li>
      );
    }
    return (
      <li key={key}>
        <NavigationMenuLink asChild>
          <LocaleLink
            href={child.path || '#'}
            hardNavigate={child.hardNavigate}
            className={DROPDOWN_ITEM_CLASS}
            data-active={isActive(child.path) || undefined}
            onClick={() => handleNavClick(child)}
          >
            {titleFor(child)}
          </LocaleLink>
        </NavigationMenuLink>
      </li>
    );
  };

  /**
   * Render the badge for a top-level link, sourced via badgeSource.
   * Currently only watchlist-unread is wired up; extend here as new sources arrive.
   */
  const renderBadge = (item: MenuItem) => {
    if (item.badgeSource === 'watchlist-unread' && watchlistUnread > 0) {
      return (
        <span
          className="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1 leading-none"
          aria-label={`${watchlistUnread} unread`}
        >
          {watchlistUnread > 99 ? '99+' : watchlistUnread}
        </span>
      );
    }
    return null;
  };

  /**
   * A top-level menu item. Three shapes are possible:
   *   1. Plain link (no children) → NavigationMenuLink
   *   2. isMegaPanel + children   → mega-grid dropdown via MegaMenuSubApps
   *   3. children                 → simple flat dropdown
   */
  const renderTopLevel = (item: MenuItem, index: number) => {
    const isPlainLink = !item.children || item.children.length === 0;

    if (isPlainLink) {
      const badge = renderBadge(item);
      return (
        <NavigationMenuItem key={index}>
          {/* LINK_CLASS goes on NavigationMenuLink (not LocaleLink) so its cn()
              tailwind-merges it over the primitive's base styles. */}
          <NavigationMenuLink asChild className={LINK_CLASS}>
            <LocaleLink
              href={item.path || '#'}
              data-active={isActive(item.path) || undefined}
              onClick={() => handleNavClick(item)}
            >
              {badge ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  {titleFor(item)}
                  {badge}
                </span>
              ) : (
                titleFor(item)
              )}
            </LocaleLink>
          </NavigationMenuLink>
        </NavigationMenuItem>
      );
    }

    const triggerActive =
      hasActiveChild(item.children) ||
      (item.path && isActive(item.path)) ||
      undefined;

    if (item.isMegaPanel) {
      return (
        <NavigationMenuItem key={index}>
          <NavigationMenuTrigger
            className={cn(LINK_CLASS, 'bg-transparent')}
            data-active={triggerActive}
            onClick={() => {
              handleNavClick(item);
              if (item.path) {
                router.push(localePath(item.path));
              }
            }}
          >
            {titleFor(item)}
          </NavigationMenuTrigger>
          <NavigationMenuContent className="p-0">
            <MegaMenuSubApps items={[item]} title={item.title} />
          </NavigationMenuContent>
        </NavigationMenuItem>
      );
    }

    return (
      <NavigationMenuItem key={index}>
        <NavigationMenuTrigger
          className={cn(LINK_CLASS, 'bg-transparent')}
          data-active={triggerActive}
          onClick={() => handleNavClick(item)}
        >
          {titleFor(item)}
        </NavigationMenuTrigger>
        <NavigationMenuContent className="p-2 min-w-[220px]">
          <ul className="flex flex-col">
            {(item.children || []).map((child, childIdx) =>
              renderDropdownChild(child, childIdx),
            )}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  };

  return (
    <NavigationMenu
      viewport={true}
      // `fill` lets the menu stretch across its (flex-1) container so the
      // top-level items spread out evenly instead of hugging content width.
      // Radix wraps the list in an unstyled div that shrinks to its content;
      // stretch it too, or the items bunch up in the middle of the space.
      className={fill ? 'max-w-none w-full [&>div]:w-full' : undefined}
    >
      <NavigationMenuIndicator />
      <NavigationMenuList className={fill ? 'w-full justify-evenly gap-6' : 'gap-6'}>
        {filteredMenu.map((item, index) => renderTopLevel(item, index))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
